import type { AccessLevel, WebsiteAccess } from "~/types/api/user/accesslevel"

interface UserStore {
    user: number | undefined,
    selectedWebsite: selectedWebsite | undefined,
    selectedClient: selectedClient | undefined,
    accessLevel: {client_id: number, name: string, websites: {id: number, name: string}[]}[]
}

export interface selectedClient {
    id: number,
    name: string
}

export interface selectedWebsite {
    id: number,
    name: string
}

export const useUserStore = defineStore('user', {
    state: (): UserStore => ({
        user: undefined,
        selectedWebsite: undefined,
        selectedClient: undefined,
        accessLevel: []
    }),
    getters: {
        availableWebsites(): WebsiteAccess[] {
            const client = this.accessLevel.find((item: {client_id: number}) => item.client_id === this.selectedClient?.id)
            return client?.websites || []
        }
    },
    actions: {
        setUser(user: number) {
            this.user = user
        },
        setSelectedWebsite(website: number) {
            if(!this.accessLevel) {
                throw createError({
                    statusCode: 404,
                    statusMessage: 'Access level not found'
                })
            }
            const client = this.accessLevel.find((item: {client_id: number}) => item.client_id === this.selectedClient?.id)
            const websiteFound = client?.websites.find((websites: {id: number}) => websites.id == Number(website)) || client?.websites[0]
            
            if(websiteFound) {
                this.selectedWebsite = websiteFound
                
                // Always save the compiere_id for syncing with SEO/Webforms
                this.saveLastSelectedCompiereId(websiteFound.id)
                
                if(import.meta.dev) {
                    console.log('Selected website set to:', this.selectedWebsite)
                }
            // Check if the website belongs to the current client
            if (this.selectedClient && client?.client_id !== this.selectedClient.id) {
                if(import.meta.dev) {
                    console.log('Website does not belong to current client, switching to correct client')
                }
                // Website doesn't belong to current client, switch to the correct client
                this.setSelectedClient(client!.client_id)
            }
            } else {
                console.error('Website not found!')
                throw createError({
                    statusCode: 404,
                    statusMessage: 'Website not found'
                })
            }
        },
        setSelectedClient(client: number) {
            if(!this.accessLevel) {
                throw createError({
                    statusCode: 404,
                    statusMessage: 'Access level not found'
                })
            }
            if(!client) {
                throw createError({
                    statusCode: 404,
                    statusMessage: 'Client param missing'
                })
            }
            const clientFound = this.accessLevel.find((item: {client_id: number}) => item.client_id === client)
            if(clientFound) {
                const clientChanged = this.selectedClient?.id !== clientFound.client_id
                this.selectedClient = {id: clientFound.client_id, name: clientFound.name}
                if(import.meta.dev) {
                    console.log('Client set to:', this.selectedClient)
                }
                if(clientChanged || !this.selectedWebsite) {
                    this.setSelectedWebsite(clientFound.websites[0]!.id)
                }
            } else {
                throw createError({
                    statusCode: 404,
                    statusMessage: 'Client not found'
                })
            }
        },
        setClientAndWebsiteFromWebsite(websiteId: number) {
            if(!this.accessLevel) {
                throw createError({
                    statusCode: 404,
                    statusMessage: 'Access level not found'
                })
            }
            
            if(!websiteId) {
                throw createError({
                    statusCode: 404,
                    statusMessage: 'Website ID param missing'
                })
            }
            
            // Find the client that contains this website
            const clientWithWebsite = this.accessLevel
                .map(client => ({
                    client,
                    website: client.websites.find((w: {id: number}) => w.id === Number(websiteId))
                }))
                .find(result => result.website !== undefined)
            
            if(!clientWithWebsite?.website) {
                throw createError({
                    statusCode: 404,
                    statusMessage: 'Website not found in any accessible client'
                })
            }
            
            // Set both client and website
            this.selectedClient = {id: clientWithWebsite.client.client_id, name: clientWithWebsite.client.name}
            this.selectedWebsite = {id: clientWithWebsite.website.id, name: clientWithWebsite.website.name}
            if(import.meta.dev) {
                console.log('Client and website set from a website ID:', this.selectedClient, this.selectedWebsite)
            }
        },
        async setAccessLevel() {
            try {
            const accessLevel = await $fetch<AccessLevel[]>('/api/user/accesslevel', {
                headers: useRequestHeaders()
            })
            this.accessLevel = accessLevel.map((item: any) => {
                return {
                    client_id: item.id,
                    name: item.name,
                    websites: item.websites_access
                }
            })
            return this.accessLevel
            } catch(err) {
                if(err.statusCode == 403) {
                   
                    throw createError({
                        statusCode: 403,
                        statusMessage: 'Access denied'
                    })
                } else {
                    throw createError({
                        statusCode: 500,
                        statusMessage: 'Error setting access level',
                    })
                }
            }
        },
/**
         * Get the last selected compiere_id (website ID - shared across all pages)
         * Reads from localStorage for persistence across browser sessions
         * @returns The compiere_id (website ID) or undefined
         */
        getLastSelectedCompiereId(): number | undefined {
            // Try localStorage first (persists across sessions)
            if (import.meta.client) {
                const stored = localStorage.getItem('lastSelectedCompiereId')
                if (stored) {
                    const compiereId = parseInt(stored, 10)
                    if (!isNaN(compiereId)) {
                        // Verify user still has access to this compiere_id
                        const hasAccess = this.accessLevel.some(c => 
                            c.websites?.some(w => w.id === compiereId)
                        )
                        if (hasAccess) {
                            return compiereId
                        }
                        // User lost access, clear the stored value
                        localStorage.removeItem('lastSelectedCompiereId')
                    }
                }
            }
            return undefined
        },
        /**
         * Save the last selected compiere_id (website ID - shared across all pages)
         * Saves to localStorage for persistence across browser sessions
         * @param compiereId The website ID (compiere_id) to remember
         */
        saveLastSelectedCompiereId(compiereId: number) {
            if (import.meta.client) {
                localStorage.setItem('lastSelectedCompiereId', String(compiereId))
            }
        },
        /**
         * Initialize client selection for a page (Orders/Webusers)
         * Uses the last selected compiere_id from localStorage to find the matching client
         * @param preferredClientId Optional preferred client ID for first-time users (e.g., 1000014)
         */
        initializeClientSelection(preferredClientId?: number) {
            if (!this.accessLevel || this.accessLevel.length === 0) {
                return
            }
            
            // Check for last selected compiere_id (from localStorage)
            const lastCompiereId = this.getLastSelectedCompiereId()
            
            if (lastCompiereId !== undefined) {
                // Find the client that has this website (compiere_id)
                const clientWithWebsite = this.accessLevel.find(c => 
                    c.websites?.some(w => w.id === lastCompiereId)
                )
                if (clientWithWebsite) {
                    if (this.selectedClient?.id !== clientWithWebsite.client_id) {
                        this.setSelectedClient(clientWithWebsite.client_id)
                    }
                    return
                }
            }
            
            // Check for preferred client ID (e.g., 1000014) for first-time users
            if (preferredClientId !== undefined) {
                const preferredClient = this.accessLevel.find(c => c.client_id === preferredClientId)
                if (preferredClient) {
                    this.setSelectedClient(preferredClientId)
                    return
                }
            }
            
            // Fall back to first available client
            const firstClient = this.accessLevel[0]
            if (firstClient) {
                this.setSelectedClient(firstClient.client_id)
            }
        }
    }
  })
